/**
 * WorkforceConsolePage — the pilot's single starting point for the digital workforce
 * (DIGEMP-PILOT-COMPOSITION-FRONTEND-070).
 *
 * One read model answers "who is available, what is in progress, what needs a human and what was
 * actually verified": GET /api/agents/workforce-overview, which composes the same audited run
 * evidence the employee workspace and experience timeline already use. Every row links into the
 * page that owns the action (roster, run, attention inbox); this screen re-implements none of
 * them, and unknown evidence is shown as unknown rather than as zero.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { agentsApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import EmployeeAvatar, { STATUS_META } from "../components/EmployeeAvatar";
import { STATUS_BADGE } from "../components/EmployeeWorkspace";
import { runStatusLabel } from "../components/runStatusVocabulary";
import WorkOutcomeChip from "../components/WorkOutcomeChip";
import { SOURCE_STATES, classifySource } from "../components/statusVocabulary";
import { classifySourceTruth } from "../components/sourceTruth";
import SourceTruthBadge from "../components/SourceTruthBadge";
import { useOnlineStatus, useRecoveredNotice } from "../components/useLiveState";
import { formatWaitDuration, ticketLabel } from "./AttentionInboxPage";
import RoleSurface from "../components/designSystem/RoleSurface";

// Basis labels mirror the audited verification rule in the backend employee_experience service:
// a technician resolution note wins; otherwise an approved-and-executed close_ticket on a closed
// ticket. An unrecognised basis is shown raw rather than guessed.
export const VERIFIED_BASIS_HE = {
  technician_resolution_outcome: "הערת פתרון של טכנאי",
  approved_close_executed: "סגירת קריאה שאושרה ובוצעה",
};

export const KIND_BADGE = {
  run_failed: "bg-red-100 text-red-700",
  awaiting_approval: "bg-amber-100 text-amber-700",
  awaiting_technician_review: "bg-amber-100 text-amber-700",
  stale_customer_wait: "bg-blue-100 text-blue-700",
  paused: "bg-gray-200 text-gray-600",
};

const ATTENTION_DISPLAY_LIMIT = 5;
const RESULT_SUMMARY_MAX = 160;

const timeOf = (timestamp) => (timestamp ? new Date(timestamp).toLocaleTimeString("he-IL") : null);

// Recovered-notice semantics live in components/useLiveState.js so every live surface shares one
// definition: only an observed degradation followed by a newer successful load announces recovery.

// ─── Pure derivations (exported for focused tests) ───────────────────────────

export function readinessSummary(readiness) {
  const evidence = readiness?.evidence || {};
  const verified = evidence.verified_successes ?? 0;
  const terminal = evidence.terminal_runs ?? 0;
  const unverified = evidence.unverified_completions ?? 0;
  const failures = evidence.failures ?? 0;
  if (!readiness?.has_outcome_evidence) {
    return {
      unknown: true,
      text: "אין עדות תוצאות בחלון — לא ניתן להעריך כשירות",
      verified, terminal, unverified, failures,
    };
  }
  const rate = readiness.verified_success_rate;
  const pct = typeof rate === "number" ? Math.round(rate * 100) : null;
  return {
    unknown: false,
    text: pct == null
      ? `אומתו ${verified} מתוך ${terminal} ריצות בחלון`
      : `אומתו ${verified} מתוך ${terminal} ריצות בחלון (${pct}%)`,
    verified, terminal, unverified, failures,
  };
}

function msSince(iso, now) {
  if (!iso) return null;
  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? null : Math.max(0, now - parsed);
}

export function attentionAgeText(item, now) {
  const duration = formatWaitDuration(
    msSince(item?.since, now) ?? (typeof item?.age_seconds === "number" ? Math.max(0, item.age_seconds * 1000) : null)
  );
  if (duration == null) return "משך הזמן אינו ידוע";
  if (item?.kind === "run_failed") return `נכשל לפני ${duration}`;
  if (item?.kind === "paused") return `מושהה מזה ${duration}`;
  return `ממתין כבר ${duration}`;
}

export function verifiedAgeText(outcome, now) {
  const duration = formatWaitDuration(
    msSince(outcome?.verified_at, now) ??
      (typeof outcome?.age_seconds === "number" ? Math.max(0, outcome.age_seconds * 1000) : null)
  );
  return duration == null ? "מועד האימות אינו ידוע" : `אומת לפני ${duration}`;
}

export function startedWorkText(work, now) {
  const ms = msSince(work?.started_at, now);
  return ms == null ? null : `החל לפני ${formatWaitDuration(ms)}`;
}

export function clipSummary(text) {
  if (typeof text !== "string" || text.trim() === "") return null;
  const trimmed = text.trim();
  return trimmed.length > RESULT_SUMMARY_MAX ? `${trimmed.slice(0, RESULT_SUMMARY_MAX)}…` : trimmed;
}

// ─── Building blocks ─────────────────────────────────────────────────────────

function PageNotice({ text }) {
  return (
    <div dir="rtl" className="w-full max-w-[1200px] mx-auto px-4 py-6">
      <p className="text-sm text-gray-500">{text}</p>
    </div>
  );
}

function TotalTile({ label, value, ariaLabel }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 px-3 py-2" aria-label={ariaLabel}>
      <div className="text-lg font-semibold text-gray-800">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

function TicketLinkOrText({ item, runId }) {
  const text = ticketLabel(item);
  if (item?.ticket_id) {
    return (
      <Link to={`/tickets/${item.ticket_id}`} className="hover:underline" aria-label={runId ? `${text} — ריצה ${runId}` : text}>
        {text}
      </Link>
    );
  }
  return <span className="text-gray-400">{text}</span>;
}

function EmployeeCard({ employee, now, canManage }) {
  const status = employee.effective_status;
  const statusLabel = STATUS_META[status]?.label ?? null;
  const work = employee.current_work;
  const readiness = readinessSummary(employee.specialist_readiness);
  const startedText = work ? startedWorkText(work, now) : null;
  const specialties = employee.specialist_readiness?.declared_specialties || [];

  return (
    <article
      className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-2"
      aria-label={`עובד דיגיטלי ${employee.name}`}
    >
      <div className="flex items-center gap-3 flex-wrap">
        <EmployeeAvatar
          name={employee.name}
          avatarKey={employee.avatar_key}
          avatarColor={employee.avatar_color}
          status={status}
          showStatus
        />
        <div className="min-w-0 flex-1 space-y-0.5">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={`/agents/${employee.agent_id}`}
              className="font-medium text-brand-600 hover:underline"
              aria-label={`לסביבת העבודה של ${employee.name}`}
            >
              {employee.name}
            </Link>
            {employee.role_title && <span className="text-xs text-violet-700">{employee.role_title}</span>}
            {statusLabel && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE[status] ?? "bg-gray-100 text-gray-600"}`}>
                {statusLabel}
              </span>
            )}
            {!employee.is_enabled && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">מושבת</span>
            )}
            {employee.awaiting_operator > 0 && (
              <Link
                to={`/agent-runs?agent_id=${employee.agent_id}&status=pending_review`}
                className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 hover:bg-orange-200"
                aria-label={`הרצות הממתינות לטיפול של ${employee.name}`}
              >
                ממתינות לטיפול: {employee.awaiting_operator}
              </Link>
            )}
            {employee.attention_count > 0 && (
              <Link
                to="/attention-inbox"
                className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200"
                aria-label={`לתיבת תשומת הלב — ${employee.name}`}
              >
                דורש טיפול: {employee.attention_count}
              </Link>
            )}
          </div>
          <div className="text-sm text-gray-700 flex items-center gap-2 flex-wrap">
            {work ? (
              <>
                <TicketLinkOrText item={work} runId={work.run_id} />
                {work.run_status && (
                  <>
                    <WorkOutcomeChip
                      status={work.run_status}
                      testId={`work-outcome-${employee.agent_id}`}
                    />
                    <span className="text-xs text-gray-500">{runStatusLabel(work.run_status)}</span>
                  </>
                )}
                {startedText && <span className="text-xs text-gray-400">{startedText}</span>}
                <Link
                  to={`/agent-runs/${work.run_id}`}
                  className="text-xs text-blue-700 hover:underline"
                  aria-label={`לריצה הפעילה של ${employee.name}`}
                >
                  לריצה
                </Link>
              </>
            ) : (
              <span className="text-gray-400">אין משימה פעילה כרגע</span>
            )}
          </div>
          {(employee.workload.active_runs > 0 ||
            employee.workload.waiting_for_customer > 0 ||
            employee.workload.paused > 0) && (
            <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
              {employee.workload.active_runs > 0 && <span>בעבודה: {employee.workload.active_runs}</span>}
              {employee.workload.waiting_for_customer > 0 && (
                <span>ממתין ללקוח: {employee.workload.waiting_for_customer}</span>
              )}
              {employee.workload.paused > 0 && <span>מושהות: {employee.workload.paused}</span>}
            </div>
          )}
          <div className="text-xs space-y-0.5">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-gray-500">התמחויות מוצהרות:</span>
              {specialties.length > 0 ? (
                specialties.map((name) => (
                  <span key={name} className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">{name}</span>
                ))
              ) : (
                <span className="text-gray-400">לא הוצהרו</span>
              )}
            </div>
            <div className={readiness.unknown ? "text-gray-400" : "text-gray-600"}>{readiness.text}</div>
            {!readiness.unknown && (readiness.unverified > 0 || readiness.failures > 0) && (
              <div className="text-gray-500">
                {readiness.unverified > 0 && `הושלמו ללא אימות: ${readiness.unverified}`}
                {readiness.unverified > 0 && readiness.failures > 0 && " · "}
                {readiness.failures > 0 && `כשלים: ${readiness.failures}`}
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

function AttentionRow({ item, now }) {
  return (
    <article
      className="bg-white rounded-lg border border-gray-200 px-3 py-2 flex items-center gap-3 flex-wrap"
      aria-label={`${item.label} — ${item.agent_name}`}
    >
      <span className={`text-xs px-2 py-0.5 rounded ${KIND_BADGE[item.kind] ?? "bg-gray-200 text-gray-600"}`}>
        {item.label}
      </span>
      {/* The attention item's own kind word above is what the item IS; this chip is the §14C.1
          outcome the same run status reads as, from the one shared vocabulary. */}
      <WorkOutcomeChip status={item.run_status} testId={`work-outcome-${item.run_id}`} />
      <Link
        to={`/agents/${item.agent_id}`}
        className="text-sm text-gray-800 hover:underline"
        aria-label={`לעובד ${item.agent_name}`}
      >
        {item.agent_name}
      </Link>
      <span className="text-sm text-gray-700">
        <TicketLinkOrText item={item} runId={item.run_id} />
      </span>
      <span className="text-xs text-gray-400">{attentionAgeText(item, now)}</span>
      <Link
        to={`/agent-runs/${item.run_id}`}
        className="ml-auto text-xs text-blue-700 hover:underline"
        aria-label={`טיפול — ${item.label} — ${ticketLabel(item)}`}
      >
        טיפול
      </Link>
    </article>
  );
}

function VerifiedOutcomeRow({ outcome, now }) {
  const basis = VERIFIED_BASIS_HE[outcome.verified_basis] ?? outcome.verified_basis ?? "בסיס אימות לא ידוע";
  const summary = clipSummary(outcome.result_summary);
  return (
    <article
      className="bg-white rounded-lg border border-gray-200 px-3 py-2 space-y-0.5"
      aria-label={`תוצאה מאומתת — ${outcome.agent_name}`}
    >
      <div className="flex items-center gap-2 flex-wrap text-sm">
        <Link
          to={`/agents/${outcome.agent_id}`}
          className="text-gray-800 hover:underline"
          aria-label={`לעובד ${outcome.agent_name}`}
        >
          {outcome.agent_name}
        </Link>
        {/* This row exists only for a run the backend classified as a positive terminal status AND
            audited as verified (workforce_overview._POSITIVE_STATUSES + _verification_for), so its
            §14C.1 outcome is `completed` by the row's own contract — not guessed from a payload
            field the schema does not carry. */}
        <WorkOutcomeChip outcome="completed" testId={`work-outcome-${outcome.run_id}`} />
        <span className="text-gray-700">
          <TicketLinkOrText item={outcome} runId={outcome.run_id} />
        </span>
        <span className="text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">{basis}</span>
        <span className="text-xs text-gray-400">{verifiedAgeText(outcome, now)}</span>
        <Link
          to={`/agent-runs/${outcome.run_id}`}
          className="ml-auto text-xs text-blue-700 hover:underline"
          aria-label={`לריצה המאומתת — ${ticketLabel(outcome)}`}
        >
          לריצה
        </Link>
      </div>
      {summary && <div className="text-xs text-gray-500">{summary}</div>}
    </article>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function WorkforceConsolePage() {
  const { can, loading: permsLoading } = useAgentPermissions();
  const canView = can("agents_view");
  const canManage = can("agents_manage");

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(tick);
  }, []);

  const overviewQuery = useQuery({
    queryKey: ["workforce-overview"],
    queryFn: () => agentsApi.workforceOverview(),
    enabled: canView,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const online = useOnlineStatus();
  const sourceState = classifySource(overviewQuery);
  const overviewTruth = classifySourceTruth(overviewQuery, {
    online,
    pollingMs: 60_000,
    enabled: canView,
  });
  const recoveredAt = useRecoveredNotice({
    degraded: sourceState.key === SOURCE_STATES.failed || sourceState.key === SOURCE_STATES.stale,
    fresh: sourceState.key === SOURCE_STATES.fresh,
    online,
    loadedAt: overviewQuery.dataUpdatedAt,
  });

  if (permsLoading) return <PageNotice text="טוען הרשאות…" />;
  if (!canView) return <PageNotice text="אין הרשאה לצפות בכוח העבודה הדיגיטלי" />;

  const data = overviewQuery.data;
  const totals = data?.totals;
  const evidence = data?.outcome_evidence;
  const employees = data?.employees || [];
  const attention = data?.attention || [];
  const verified = data?.recent_verified_outcomes || [];
  const shownAttention = attention.slice(0, ATTENTION_DISPLAY_LIMIT);

  return (
    <RoleSurface
      role="manager"
      dir="rtl"
      title="כוח העבודה הדיגיטלי"
      description="מי זמין, מה בעבודה, מה ממתין לטיפול אנושי ומה אומת בפועל — נקודת הפתיחה של הטייס. כל פריט כאן מקשר למסך שבו מתבצעת הפעולה."
      actions={
        <>
          <SourceTruthBadge truth={overviewTruth} testId="workforce-source-truth" />
          <Link to="/agents" className="text-sm text-brand-600 hover:underline">
            לניהול העובדים
          </Link>
          <button
            type="button"
            onClick={() => overviewQuery.refetch()}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50"
          >
            רענן
          </button>
        </>
      }
      meta={
        overviewQuery.dataUpdatedAt > 0
          ? `נטען לאחרונה: ${new Date(overviewQuery.dataUpdatedAt).toLocaleTimeString("he-IL")}`
          : null
      }
      testId="workforce-role-surface"
    >

      {sourceState.key === SOURCE_STATES.failed && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm p-3 flex items-center gap-3 flex-wrap">
          <span>
            {online
              ? "לא ניתן לטעון את מצב כוח העבודה — הנתונים אינם ידועים כעת."
              : "אין חיבור לרשת — לא ניתן לטעון את מצב כוח העבודה. הנתונים אינם ידועים כעת."}
          </span>
          <button type="button" onClick={() => overviewQuery.refetch()} className="underline">
            נסה שוב
          </button>
        </div>
      )}

      {sourceState.key === SOURCE_STATES.stale && (
        <div role="status" className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm p-3 flex items-center gap-3 flex-wrap">
          <span>
            הרענון נכשל — מוצג הנתון מהטעינה המוצלחת מ־{timeOf(sourceState.since)}. ייתכן שהמצב אינו מעודכן.
          </span>
          {online && (
            <button type="button" onClick={() => overviewQuery.refetch()} className="underline">
              נסה שוב
            </button>
          )}
        </div>
      )}

      {!online && data && (
        <p role="status" className="text-xs rounded-lg border border-gray-300 bg-gray-100 text-gray-700 p-3">
          אין חיבור לרשת — הרענון מושהה עד לחזרת החיבור. הנתונים המוצגים מהטעינה מ־
          {timeOf(overviewQuery.dataUpdatedAt)}.
        </p>
      )}

      {!data && sourceState.key === SOURCE_STATES.loading && (
        <p role="status" className="text-sm text-gray-400">
          {overviewQuery.fetchStatus === "paused"
            ? "אין חיבור לרשת — ממתין לחיבור לפני טעינת מצב כוח העבודה."
            : "טוען את כוח העבודה…"}
        </p>
      )}

      {recoveredAt != null && (
        <p role="status" className="text-xs rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 p-3">
          החיבור שוחזר — מצב כוח העבודה רוענן בהצלחה ב־{timeOf(recoveredAt)}.
        </p>
      )}

      {data && (
        <>
          <section aria-label="סיכום כוח העבודה" className="space-y-2">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2" role="group" aria-label="מונה עובדים">
              <TotalTile label="עובדים" value={totals.employees} ariaLabel="סך העובדים הדיגיטליים" />
              <TotalTile label="זמינים" value={totals.available} ariaLabel="עובדים זמינים" />
              <TotalTile label="עסוקים" value={totals.busy} ariaLabel="עובדים עסוקים" />
              <TotalTile label="בעבודה כעת" value={totals.active_runs} ariaLabel="ריצות פעילות" />
              <TotalTile
                label="ממתין לטיפול אנושי"
                value={totals.awaiting_operator}
                ariaLabel="ריצות הממתינות לטיפול אנושי"
              />
              <TotalTile label="ממתין ללקוח" value={totals.waiting_for_customer} ariaLabel="ריצות הממתינות ללקוח" />
            </div>
            {(totals.disabled > 0 || totals.away > 0 || totals.offline > 0) && (
              <div className="text-xs text-gray-500 flex items-center gap-3 flex-wrap">
                {totals.disabled > 0 && <span>מושבתים: {totals.disabled}</span>}
                {totals.away > 0 && <span>לא כאן: {totals.away}</span>}
                {totals.offline > 0 && <span>מנותקים: {totals.offline}</span>}
              </div>
            )}
            <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 space-y-0.5" aria-label="עדויות תוצאה">
              <div className="flex items-center gap-3 text-xs text-gray-600 flex-wrap">
                <span>אומתו: {totals.verified_successes}</span>
                <span>הושלמו ללא אימות: {totals.unverified_completions}</span>
                <span>נכשלו: {totals.failures}</span>
                <span>בוטלו: {totals.cancellations}</span>
              </div>
              <p className="text-xs text-gray-400">
                חלון הנתונים: {data.window_days} ימים · נסרקו {evidence.scanned} ריצות שהסתיימו
              </p>
              {evidence.truncated && (
                <p className="text-xs text-amber-700">
                  הסריקה נחתכה בתקרה של {evidence.cap} ריצות — ייתכן שחלק מהעדויות אינן נכללות.
                </p>
              )}
              {evidence.unmatched_runs > 0 && (
                <p className="text-xs text-amber-700">
                  ל-{evidence.unmatched_runs} ריצות שהסתיימו לא נמצאה התאמת עדות מלאה.
                </p>
              )}
            </div>
          </section>

          <section aria-label="העובדים הדיגיטליים" className="space-y-2">
            <h2 className="text-base font-semibold text-gray-800">העובדים הדיגיטליים</h2>
            {employees.length === 0 ? (
              <p className="text-sm text-gray-500">
                לא נמצאו עובדים דיגיטליים.{" "}
                {canManage && (
                  <Link to="/employee-builder" className="text-brand-600 hover:underline">
                    יצירת עובד דיגיטלי
                  </Link>
                )}
              </p>
            ) : (
              <div className="space-y-2">
                {employees.map((employee) => (
                  <EmployeeCard key={employee.agent_id} employee={employee} now={now} canManage={canManage} />
                ))}
              </div>
            )}
          </section>

          <section aria-label="דורש טיפול אנושי" className="space-y-2">
            <h2 className="text-base font-semibold text-gray-800">דורש טיפול אנושי</h2>
            <p className="text-xs text-gray-500">
              הפריטים הדחופים ביותר מכלל ההמתנות, הכשלים וההסלמות בחלון. הטיפול עצמו מתבצע במסך
              הריצה; התור המלא בתיבת תשומת הלב.
            </p>
            {attention.length === 0 ? (
              <p className="text-sm text-gray-500">אין פריטים דחופים כרגע.</p>
            ) : (
              <>
                <div className="space-y-1.5">
                  {shownAttention.map((item) => (
                    <AttentionRow key={`${item.kind}-${item.run_id}`} item={item} now={now} />
                  ))}
                </div>
                <p className="text-xs text-gray-500">
                  מוצגים {shownAttention.length} מתוך {attention.length} פריטים דחופים בחלון.{" "}
                  <Link to="/attention-inbox" className="text-brand-600 hover:underline">
                    לתיבת תשומת הלב
                  </Link>
                </p>
              </>
            )}
          </section>

          <section aria-label="תוצאות מאומתות לאחרונה" className="space-y-2">
            <h2 className="text-base font-semibold text-gray-800">תוצאות מאומתות לאחרונה</h2>
            {verified.length === 0 ? (
              <p className="text-sm text-gray-500">
                לא נמצאו תוצאות מאומתות בחלון — אימות דורש הערת פתרון של טכנאי או סגירת קריאה
                מאושרת ובוצעה.
              </p>
            ) : (
              <div className="space-y-1.5">
                {verified.map((outcome) => (
                  <VerifiedOutcomeRow key={outcome.run_id} outcome={outcome} now={now} />
                ))}
              </div>
            )}
            {verified.length > 0 && (
              <p className="text-xs text-gray-500">מוצגות {verified.length} התוצאות המאומתות האחרונות בחלון.</p>
            )}
          </section>
        </>
      )}
    </RoleSurface>
  );
}
